import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';
import { Hourglass, CheckCircle, XCircle, Wallet, ClipboardList, Banknote } from 'lucide-react';

export default function AdminWithdrawals() {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [actionLoading, setActionLoading] = useState(null);

  const fetchWithdrawals = useCallback(() => {
    setLoading(true);
    api.get('/withdraw/all').then(r => setWithdrawals(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchWithdrawals(); }, [fetchWithdrawals]);

  const handleApprove = async (id) => {
    if (!window.confirm('Approve this withdrawal?')) return;
    setActionLoading(id + '_approve');
    try {
      const res = await api.put(`/withdraw/${id}/approve`);
      toast.success(res.data.message);
      fetchWithdrawals();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally { setActionLoading(null); }
  };

  const handleReject = async (id) => {
    const note = prompt('Reason for rejection:') || '';
    setActionLoading(id + '_reject');
    try {
      const res = await api.put(`/withdraw/${id}/reject`, { adminNote: note });
      toast.success(res.data.message);
      fetchWithdrawals();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally { setActionLoading(null); }
  };

  const formatCurrency = (n) => `₹${(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  const filtered = withdrawals.filter(w => filter === 'all' || w.status === filter);
  const counts = {
    pending: withdrawals.filter(w => w.status === 'pending').length,
    approved: withdrawals.filter(w => w.status === 'approved').length,
    paid: withdrawals.filter(w => w.status === 'paid').length,
    rejected: withdrawals.filter(w => w.status === 'rejected').length,
  };
  const totalPendingAmount = withdrawals.filter(w => w.status === 'pending').reduce((sum, w) => sum + w.netAmount, 0);

  return (
    <DashboardLayout>
      <h1 className="page-title">Withdrawals</h1>
      <p className="page-subtitle">Process withdrawal requests from users</p>

      <div className="stats-grid" style={{ marginBottom: 20 }}>
        {[
          { label: 'Pending', value: counts.pending, color: '#f59e0b', icon: Hourglass },
          { label: 'Approved', value: counts.approved, color: '#3b82f6', icon: CheckCircle },
          { label: 'Paid', value: counts.paid, color: '#22c55e', icon: CheckCircle },
          { label: 'Rejected', value: counts.rejected, color: '#ef4444', icon: XCircle },
          { label: 'Pending Payout', value: formatCurrency(totalPendingAmount), color: 'var(--primary)', icon: Wallet },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-icon" style={{ background: `${s.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: s.color }}><s.icon size={22} /></div>
            <div className="stat-info">
              <div className="stat-label">{s.label}</div>
              <div className="stat-value" style={{ color: s.color, fontSize: '1.1rem' }}>{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="tab-bar" style={{ marginBottom: 20 }}>
        {[['pending', Hourglass, 'Pending'], ['approved', CheckCircle, 'Approved'], ['paid', CheckCircle, 'Paid'], ['rejected', XCircle, 'Rejected'], ['all', ClipboardList, 'All']].map(([id, Icon, label]) => (
          <button key={id} className={`tab-btn ${filter === id ? 'active' : ''}`} onClick={() => setFilter(id)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Icon size={14} /> {label} ({id === 'all' ? withdrawals.length : counts[id] || 0})
          </button>
        ))}
      </div>

      <div className="card">
        {loading ? <div className="loader-wrap"><div className="loader"></div></div> :
          filtered.length === 0 ? (
            <div className="empty-state"><div className="empty-icon"><Banknote size={40} /></div><h3>No withdrawals found</h3></div>
          ) : (
            <div className="table-wrap">
              <table className="tp-table">
                <thead>
                  <tr>
                    <th>Date</th><th>User</th><th>Requested</th><th>Deduction</th><th>Net Amount</th>
                    <th>Wallet</th><th>Bank Details</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(w => (
                    <tr key={w._id}>
                      <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{new Date(w.createdAt).toLocaleDateString('en-IN')}</td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{w.user?.name}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{w.user?.mobile}</div>
                      </td>
                      <td style={{ color: 'var(--primary)', fontWeight: 700 }}>{formatCurrency(w.amount)}</td>
                      <td style={{ color: '#ef4444' }}>-{formatCurrency(w.deduction)}</td>
                      <td style={{ color: '#22c55e', fontWeight: 700 }}>{formatCurrency(w.netAmount)}</td>
                      <td style={{ textTransform: 'capitalize', fontSize: '0.85rem' }}>{w.walletType}</td>
                      <td style={{ fontSize: '0.78rem', maxWidth: 150 }}>
                        {w.payoutMethod === 'USDT' ? (
                          <div>USDT: {w.bankDetails?.usdtAddress || '—'}</div>
                        ) : w.bankDetails?.upiId ? (
                          <div>UPI: {w.bankDetails.upiId}</div>
                        ) : (
                          <>
                            <div>{w.bankDetails?.bankName || '—'}</div>
                            <div style={{ color: 'var(--text-muted)' }}>{w.bankDetails?.accountNumber ? `A/C: ${w.bankDetails.accountNumber}` : '—'}</div>
                            <div style={{ color: 'var(--text-muted)' }}>{w.bankDetails?.ifscCode || ''}</div>
                          </>
                        )}
                      </td>
                      <td>
                        <span className={`badge badge-${w.status === 'paid' ? 'success' : w.status === 'approved' ? 'info' : w.status === 'rejected' ? 'danger' : 'warning'}`}>
                          {w.status}
                        </span>
                        {w.adminNote && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>{w.adminNote}</div>}
                      </td>
                      <td>
                        {w.status === 'pending' ? (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => handleApprove(w._id)}
                              disabled={actionLoading === w._id + '_approve'}
                              style={{ fontSize: '0.75rem', padding: '6px 10px' }}
                            >
                              {actionLoading === w._id + '_approve' ? '...' : (<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CheckCircle size={14} /> Pay</span>)}
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleReject(w._id)}
                              disabled={actionLoading === w._id + '_reject'}
                              style={{ fontSize: '0.75rem', padding: '6px 10px' }}
                            >
                              {actionLoading === w._id + '_reject' ? '...' : (<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><XCircle size={14} /> Reject</span>)}
                            </button>
                          </div>
                        ) : (
                          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                            {w.status === 'paid' && w.paidAt
                              ? `Paid: ${new Date(w.paidAt).toLocaleDateString('en-IN')}`
                              : w.processedAt ? new Date(w.processedAt).toLocaleDateString('en-IN') : 'Processed'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </div>
    </DashboardLayout>
  );
}